import React from 'react';
import styled from 'styled-components';
import { CreditCard } from '../creditCard/CreditCard';
import { CreditCardCVV } from '../creditCardCVV/creditCardCVV';
import { CreditCardExpiration } from '../creditCardExpiration/creditCardExpiration';
import Input2, { InputBackground } from '../input/Input2';
import InputGroup from '../inputGroup/InputGroup';
import { PrivacyOptions } from '@sunbit-dev/common/enums/Datadog';

// ############################ Styles ###########################
// tslint:disable:variable-name
const SCardSection = styled.section(({ theme }) => ({
  marginBottom: theme.spacing.large,
}));

const SCreditCard = styled(CreditCard)(({ theme }) => ({
  width: '100%',
  maxWidth: 550,
  marginBottom: theme.spacing.medium,
}));

const SCardExpirationCvvSection = styled.section(() => ({
  width: '100%',
  maxWidth: 400,
  display: 'flex',
  justifyContent: 'flex-start',
}));

const SCreditCardExpiration = styled(CreditCardExpiration)(({ theme }) => ({
  marginRight: theme.spacing.larger,
  flexGrow: 3,
}));

const SCreditCardCVV = styled(CreditCardCVV)(() => ({
  flexGrow: 2,
}));
// tslint:enable:variable-name
// ############################ End Styles ###########################

interface ICreditCardFormValues {
  cardNumber: string;
  creditCardExpiration: string;
  cvv: string;
  firstName: string;
  middleName: string;
  lastName: string;
}

interface ICreditCardFormProps {
  onChange: (value: string, name: string) => void;
  setDefaultValues?: (debitDetails: ICreditCardFormValues) => void;
  hasName?: boolean;
}

declare const window: Window & { sunbitEnv: string };

// tslint:disable-next-line:variable-name
const CreditCardForm: React.FC<ICreditCardFormProps> = (props) => {
  const defaultDetails: ICreditCardFormValues =
    window.sunbitEnv === 'oregon-demo'
      ? {
          cardNumber: '4000056655665556',
          creditCardExpiration: '1128',
          cvv: '321',
          firstName: '',
          middleName: '',
          lastName: '',
        }
      : {
          cardNumber: '',
          creditCardExpiration: '',
          cvv: '',
          firstName: '',
          middleName: '',
          lastName: '',
        };

  const [values, setValues] = React.useState<ICreditCardFormValues>(defaultDetails);

  const handleOnChange = React.useCallback(
    (value, name) => {
      setValues({ ...values, [name]: value });
      props.onChange(value, name);
    },
    [...Object.values(values)]
  );

  const handleOnChangeEvent = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      handleOnChange(value, name);
    },
    [handleOnChange]
  );

  React.useEffect(() => {
    // Set default props for demo env
    if (props.setDefaultValues) {
      props.setDefaultValues(defaultDetails);
    }
  }, []);

  return (
    <SCardSection data-dd-privacy={PrivacyOptions.MaskUserInput}>
      <SCreditCard onFilled={handleOnChange} value={values.cardNumber} />
      <SCardExpirationCvvSection>
        <SCreditCardExpiration onFilled={handleOnChange} value={values.creditCardExpiration} />
        <SCreditCardCVV onFilled={handleOnChange} value={values.cvv} dataTestId="credit-card-cvv-input" />
      </SCardExpirationCvvSection>
      {props.hasName && (
        <InputGroup labelColor={'primary'} dataAt="customerDetailsInputs1" label="Cardholder Name">
          <Input2
            color="primary"
            autoComplete="cc-given-name"
            dataAt="firstName"
            name="firstName"
            background={InputBackground.White}
            className="inputInGroup"
            onChange={handleOnChangeEvent}
            value={values.firstName || ''}
            placeholder="First Name"
          />
          <Input2
            color="primary"
            autoComplete="cc-additional-name"
            dataAt="middleName"
            name="middleName"
            background={InputBackground.White}
            className="inputInGroup"
            onChange={handleOnChangeEvent}
            value={values.middleName || ''}
            placeholder="Middle Name"
          />
          <Input2
            color="primary"
            autoComplete="cc-family-name"
            dataAt="lastName"
            name="lastName"
            background={InputBackground.White}
            className="inputInGroup"
            onChange={handleOnChangeEvent}
            value={values.lastName || ''}
            placeholder="Last Name"
          />
        </InputGroup>
      )}
    </SCardSection>
  );
};

export default CreditCardForm;
